import React, { useState } from 'react';

const TESTER_API = window.location.hostname === 'localhost'
  ? 'http://localhost:3001'
  : '/api/proxy-tester';

const ProxyTesterPage = () => {
  const [proxyInput, setProxyInput] = useState('');
  const [protocol, setProtocol] = useState('auto');
  const [testing, setTesting] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState('');

  const runTest = async () => {
    if (!proxyInput.trim()) return;
    setError('');
    setTesting(true);

    try {
      const resp = await fetch(`${TESTER_API}/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ proxy: proxyInput.trim(), protocol }),
      });

      const data = await resp.json();

      if (resp.status === 429) {
        setError(data.error || 'Rate limited. Please wait.');
        setTesting(false);
        return;
      }

      if (resp.status === 503) {
        setError(data.error || 'Server busy. Try again shortly.');
        setTesting(false);
        return;
      }

      if (resp.status === 400) {
        setError(data.error || 'Invalid input.');
        setTesting(false);
        return;
      }

      const result = {
        id: Date.now(),
        proxy: proxyInput.trim().replace(/:[^:@]+@/, ':***@').replace(/:([^:]+)$/, ':***'),
        timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }),
        ...data,
      };

      setResults(prev => [result, ...prev].slice(0, 20));
    } catch (err) {
      setError('Failed to connect to proxy tester service.');
    }

    setTesting(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !testing) {
      runTest();
    }
  };

  const clearResults = () => {
    setResults([]);
  };

  return (
    <div className="proxy-tester-page">
      <h1 className="page-title">Proxy Tester</h1>

      <div className="proxy-info-card">
        <div className="proxy-info-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
          </svg>
        </div>
        <div className="proxy-info-content">
          <h2>Proxy Tester</h2>
          <p>Test any HTTP or SOCKS5 proxy. Enter your proxy details below and click Test to verify connectivity. Max 5 tests per minute.</p>
        </div>
      </div>

      <div className="tester-layout">
        <div className="tester-input-section">
          <div className="config-section">
            <h3 className="section-number">01. PROXY DETAILS</h3>

            <div className="form-group">
              <label>PROXY STRING</label>
              <input
                type="text"
                className="tester-input"
                placeholder="host:port:username:password"
                value={proxyInput}
                onChange={(e) => setProxyInput(e.target.value)}
                onKeyDown={handleKeyDown}
                maxLength={500}
                disabled={testing}
              />
              <p className="form-hint">
                Supported formats: host:port:user:pass, user:pass@host:port, host:port@user:pass, socks5://host:port:user:pass, host:port (no auth)
              </p>
            </div>

            <div className="format-grid">
              <div className="form-group">
                <label>PROTOCOL</label>
                <select value={protocol} onChange={(e) => setProtocol(e.target.value)} disabled={testing}>
                  <option value="auto">Auto Detect</option>
                  <option value="http">HTTP / HTTPS</option>
                  <option value="socks5">SOCKS5</option>
                </select>
                <p className="form-hint">Select 'Auto Detect' to determine protocol from the proxy string prefix or default to HTTP.</p>
              </div>

              <div className="form-group">
                <label>TEST TARGET</label>
                <input type="text" value="httpbin.org/ip" disabled className="tester-input disabled" />
                <p className="form-hint">All tests are performed against httpbin.org to verify proxy connectivity.</p>
              </div>
            </div>

            <button
              className={`test-proxy-btn ${testing ? 'testing' : ''}`}
              onClick={runTest}
              disabled={testing || !proxyInput.trim()}
            >
              {testing ? (
                <>
                  <svg className="spin" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                  </svg>
                  TESTING...
                </>
              ) : (
                <>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                  </svg>
                  TEST PROXY
                </>
              )}
            </button>

            {error && (
              <div className="tester-error">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="15" y1="9" x2="9" y2="15"/>
                  <line x1="9" y1="9" x2="15" y2="15"/>
                </svg>
                {error}
              </div>
            )}
          </div>
        </div>

        <div className="tester-results-section">
          <div className="config-section">
            <div className="results-header">
              <h3 className="section-number">02. TEST RESULTS</h3>
              {results.length > 0 && (
                <button className="clear-results-btn" onClick={clearResults}>CLEAR</button>
              )}
            </div>

            {results.length === 0 ? (
              <div className="no-results">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                  <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
                </svg>
                <p>NO TESTS YET</p>
                <span>Enter a proxy and click Test to get started</span>
              </div>
            ) : (
              <div className="results-list">
                {results.map((result) => (
                  <div key={result.id} className={`result-card ${result.success ? 'pass' : 'fail'}`}>
                    <div className="result-status">
                      <span className={`status-indicator ${result.success ? 'pass' : 'fail'}`}>
                        {result.success ? 'PASS' : 'FAIL'}
                      </span>
                      <span className="result-time-stamp">{result.timestamp}</span>
                    </div>

                    <div className="result-details">
                      {result.success ? (
                        <>
                          <div className="result-row">
                            <span className="result-label">Exit IP</span>
                            <span className="result-value">{result.ip}</span>
                          </div>
                          <div className="result-row">
                            <span className="result-label">Response Time</span>
                            <span className="result-value">{result.time}ms</span>
                          </div>
                          <div className="result-row">
                            <span className="result-label">Protocol</span>
                            <span className="result-value">{result.protocol}</span>
                          </div>
                          <div className="result-row">
                            <span className="result-label">HTTP Status</span>
                            <span className="result-value">{result.httpStatus}</span>
                          </div>
                        </>
                      ) : (
                        <div className="result-row">
                          <span className="result-label">Error</span>
                          <span className="result-value error">{result.error}</span>
                        </div>
                      )}
                      {result.time > 0 && !result.success && (
                        <div className="result-row">
                          <span className="result-label">Time</span>
                          <span className="result-value">{result.time}ms</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProxyTesterPage;
